import React, { useEffect, useState } from 'react';
import { Save, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: '',
    phone: '',
    email: '',
    address: '',
    social_links: {},
    footer_text: '',
    google_maps_key: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/settings`, settings, { headers });
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Einstellungen</h1>
        <p className="text-slate-600">Verwalte die allgemeinen Website-Einstellungen.</p>
      </div>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Company Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Unternehmensdaten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  value={settings.company_name}
                  onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  value={settings.logo_url}
                  onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                  className="mt-1"
                />
                {settings.logo_url && (
                  <img src={settings.logo_url} alt="Logo" className="mt-2 h-12 w-auto" />
                )}
              </div>
              <div>
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  value={settings.address}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>Kontaktdaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={settings.phone}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="footer_text">Footer Text</Label>
                <Input
                  id="footer_text"
                  value={settings.footer_text}
                  onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Social Links */}
          <Card>
            <CardHeader>
              <CardTitle>Social Media</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="youtube">YouTube</Label>
                <Input
                  id="youtube"
                  value={settings.social_links?.youtube || ''}
                  onChange={(e) => setSettings({
                    ...settings, 
                    social_links: {...settings.social_links, youtube: e.target.value}
                  })}
                  className="mt-1"
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div>
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  value={settings.social_links?.instagram || ''}
                  onChange={(e) => setSettings({
                    ...settings, 
                    social_links: {...settings.social_links, instagram: e.target.value}
                  })}
                  className="mt-1"
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div>
                <Label htmlFor="facebook">Facebook</Label>
                <Input
                  id="facebook"
                  value={settings.social_links?.facebook || ''}
                  onChange={(e) => setSettings({
                    ...settings, 
                    social_links: {...settings.social_links, facebook: e.target.value}
                  })}
                  className="mt-1"
                  placeholder="https://facebook.com/..."
                />
              </div>
            </CardContent>
          </Card>

          {/* API Keys */}
          <Card>
            <CardHeader>
              <CardTitle>API Einstellungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="google_maps_key">Google Maps API Key</Label>
                <Input
                  id="google_maps_key"
                  value={settings.google_maps_key}
                  onChange={(e) => setSettings({...settings, google_maps_key: e.target.value})}
                  className="mt-1"
                  placeholder="AIza..."
                />
                <p className="text-sm text-slate-500 mt-1">
                  Wird für die Kartenanzeige auf den Standortseiten benötigt.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 flex justify-end">
          <Button type="submit" disabled={saving} className="px-8">
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="spinner w-4 h-4"></div>
                Wird gespeichert...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Einstellungen speichern
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
